import React, { useState } from "react";
import { Brain, Lightbulb, ListChecks, Quote, Boxes, Layers, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { aiApi } from "@/lib/apiClient";
import { useTranslation } from "@/context/LanguageContext";
import { toast } from "sonner";

const LANGS = ["auto", "English", "German", "Croatian", "Spanish", "French", "Italian"];

const Section = ({ icon: Icon, title, children, testid }) => (
    <div className="rounded-2xl border border-border bg-surface-1 p-4" data-testid={testid}>
        <div className="mb-3 flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-surface-3 text-[hsl(var(--primary))]"><Icon className="h-4 w-4" /></div>
            <h4 className="font-sans-display text-sm font-semibold text-t1">{title}</h4>
        </div>
        {children}
    </div>
);

export const KnowledgePanel = ({ docId }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [lang, setLang] = useState("auto");

    const load = async (language = lang, refresh = false) => {
        setLoading(true);
        try {
            const res = await aiApi.extraction(docId, language, refresh);
            setData(res.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("knowledge.error"));
        } finally {
            setLoading(false);
        }
    };

    const empty = !data && !loading;

    return (
        <div className="flex h-full flex-col">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div className="flex items-center gap-2 text-t1">
                    <Brain className="h-4 w-4 text-[hsl(var(--primary))]" />
                    <span className="font-sans-display text-sm font-semibold">{t("knowledge.title")}</span>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={lang} onValueChange={(l) => { setLang(l); if (data) load(l, true); }}>
                        <SelectTrigger data-testid="knowledge-lang-select" className="h-8 w-[104px] bg-surface-2 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{LANGS.map((l) => <SelectItem key={l} value={l}>{l === "auto" ? t("summaries.auto") : l}</SelectItem>)}</SelectContent>
                    </Select>
                    {data && (
                        <Button data-testid="knowledge-refresh-button" variant="ghost" size="icon" className="text-t4 hover:text-t1" disabled={loading} onClick={() => load(lang, true)}>
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        </Button>
                    )}
                </div>
            </div>

            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="knowledge-content">
                {empty && (
                    <div className="flex h-full flex-col items-center justify-center py-10 text-center">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-2 text-[hsl(var(--primary))]"><Brain className="h-6 w-6" /></div>
                        <p className="mt-4 max-w-xs text-sm text-t3">{t("knowledge.empty")}</p>
                        <Button data-testid="knowledge-generate-button" className="mt-4 gap-2" onClick={() => load()}><Brain className="h-4 w-4" /> {t("knowledge.generate")}</Button>
                    </div>
                )}

                {loading && !data && (
                    <div className="space-y-3">
                        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full rounded-2xl bg-surface-2" />)}
                    </div>
                )}

                {data && (
                    <>
                        <Section icon={Lightbulb} title={t("knowledge.insights")} testid="knowledge-insights">
                            <div className="space-y-2.5">
                                {(data.key_insights || []).map((it, i) => (
                                    <div key={i} className="rounded-xl bg-surface-2 p-3">
                                        <div className="text-sm font-medium text-t1">{it.title || it}</div>
                                        {it.detail && <div className="mt-1 font-reading text-[14px] leading-relaxed text-t3">{it.detail}</div>}
                                    </div>
                                ))}
                            </div>
                        </Section>

                        <Section icon={ListChecks} title={t("knowledge.actions")} testid="knowledge-actions">
                            <ul className="space-y-2">
                                {(data.action_items || []).map((a, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-t2">
                                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--primary))]" />
                                        <span>{typeof a === "string" ? a : a.text}</span>
                                    </li>
                                ))}
                            </ul>
                        </Section>

                        <Section icon={Quote} title={t("knowledge.quotes")} testid="knowledge-quotes">
                            <div className="space-y-2.5">
                                {(data.quotes || []).map((q, i) => (
                                    <blockquote key={i} className="border-l-2 border-[hsl(var(--primary))] pl-3">
                                        <p className="font-reading text-[14px] italic leading-relaxed text-t1">“{q.text || q}”</p>
                                        {q.note && <p className="mt-1 text-xs text-t4">{q.note}</p>}
                                    </blockquote>
                                ))}
                            </div>
                        </Section>

                        <Section icon={Boxes} title={t("knowledge.concepts")} testid="knowledge-concepts">
                            <div className="space-y-2">
                                {(data.concepts || []).map((c, i) => (
                                    <div key={i} className="text-sm">
                                        <span className="font-medium text-t1">{c.term || c}</span>
                                        {c.definition && <span className="text-t3"> — {c.definition}</span>}
                                    </div>
                                ))}
                            </div>
                        </Section>

                        <Section icon={Layers} title={t("knowledge.models")} testid="knowledge-models">
                            <div className="space-y-2.5">
                                {(data.mental_models || []).map((m, i) => (
                                    <div key={i} className="rounded-xl bg-surface-2 p-3">
                                        <div className="text-sm font-medium text-t1">{m.name || m}</div>
                                        {m.explanation && <div className="mt-1 font-reading text-[14px] leading-relaxed text-t3">{m.explanation}</div>}
                                    </div>
                                ))}
                            </div>
                        </Section>
                    </>
                )}
            </div>
        </div>
    );
};
